import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import {
  HeartPulse, Salad, User2, Sparkles, Calendar, ArrowRight, Loader2, CalendarCheck2, Clock,
  ShieldCheck, History as HistoryIcon
} from 'lucide-react';
import { API } from '../../App';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';

const SESSION_TYPES = [
  { key: 'fisioterapia', label: 'Fisioterapia', color: 'sky', icon: HeartPulse, desc: 'Recupere-se com um fisioterapeuta parceiro.' },
  { key: 'nutricao', label: 'Nutrição', color: 'emerald', icon: Salad, desc: 'Consulta nutricional com profissional credenciado.' },
  { key: 'personal', label: 'Personal Trainer', color: 'orange', icon: User2, desc: 'Treino guiado por um Personal parceiro.' },
];

const COLOR_STYLES = {
  sky:     { bg: 'bg-sky-50', ring: 'ring-sky-200', text: 'text-sky-700', btn: 'bg-sky-600 hover:bg-sky-700' },
  emerald: { bg: 'bg-emerald-50', ring: 'ring-emerald-200', text: 'text-emerald-700', btn: 'bg-emerald-600 hover:bg-emerald-700' },
  orange:  { bg: 'bg-orange-50', ring: 'ring-orange-200', text: 'text-orange-700', btn: 'bg-orange-600 hover:bg-orange-700' },
};

const BenefitsPage = () => {
  const navigate = useNavigate();
  const [balance, setBalance] = useState(null);
  const [history, setHistory] = useState({ requests: [] });
  const [loading, setLoading] = useState(true);

  const authHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('session_token')}` } });

  useEffect(() => {
    (async () => {
      try {
        const [b, h] = await Promise.all([
          axios.get(`${API}/vitalpass/balance`, authHeader()),
          axios.get(`${API}/vitalpass/history`, authHeader()),
        ]);
        setBalance(b.data);
        setHistory(h.data);
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Erro ao carregar benefícios');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return <div className="text-center py-8"><Loader2 className="w-6 h-6 animate-spin mx-auto text-emerald-600" /></div>;
  }

  if (!balance?.has_vitalpass) {
    return (
      <div className="p-4" data-testid="benefits-page-empty">
        <h2 className="text-2xl font-black text-slate-900 mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
          Meus Benefícios Vital
        </h2>
        <Card className="p-8 text-center bg-amber-50 border-amber-200">
          <p className="text-slate-700">Seu plano atual não inclui sessões de saúde.</p>
          <p className="text-xs text-slate-500 mt-2">
            Faça upgrade para <strong>Vital Ultra</strong> ou <strong>Vital Action Premium</strong> para
            receber sessões mensais de Fisioterapia, Nutrição ou Personal.
          </p>
          <Button className="mt-4 bg-emerald-600 hover:bg-emerald-700" onClick={() => navigate('/plans/student')}>
            Ver Planos Premium <ArrowRight className="ml-2 w-4 h-4" />
          </Button>
        </Card>
      </div>
    );
  }

  const isPremium = balance.is_premium_benefits;
  const upcoming = (history.requests || []).filter(r => r.preferred_date && (r.status || 'created') !== 'cancelled');

  return (
    <div className="p-4 space-y-6" data-testid="benefits-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-black text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
            {isPremium ? 'Meus Benefícios Vital' : 'Meus VITALPASS'}
          </h2>
          <p className="text-sm text-slate-500 mt-0.5">
            Plano <strong>{balance.plan_name}</strong> — {balance.total} {balance.unit_label_plural}/mês
          </p>
        </div>
      </div>

      {/* Hero card com saldo */}
      <Card className="p-6 bg-gradient-to-br from-emerald-600 via-teal-600 to-cyan-700 text-white overflow-hidden relative" data-testid="benefits-hero-card">
        <div className="absolute -right-8 -top-8 opacity-10">
          <Sparkles className="w-40 h-40" />
        </div>
        <div className="relative z-10 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <p className="text-[10px] uppercase font-bold tracking-wider opacity-80">Saldo Atual</p>
            <p className="text-5xl font-black leading-none mt-1">{balance.balance}</p>
            <p className="text-xs mt-1 opacity-90">de {balance.total} disponíveis</p>
          </div>
          <div>
            <p className="text-[10px] uppercase font-bold tracking-wider opacity-80">Utilizadas no ciclo</p>
            <p className="text-4xl font-black leading-none mt-1">{balance.used}</p>
          </div>
          <div>
            <p className="text-[10px] uppercase font-bold tracking-wider opacity-80">Próxima renovação</p>
            <p className="text-lg font-bold mt-2">
              {balance.cycle_end ? new Date(balance.cycle_end).toLocaleDateString('pt-BR') : '—'}
            </p>
            <p className="text-[10px] mt-0.5 opacity-70 flex items-center gap-1">
              <ShieldCheck className="w-3 h-3" /> Renovação automática
            </p>
          </div>
        </div>
      </Card>

      {/* Session type cards */}
      <div>
        <h3 className="text-lg font-bold text-slate-900 mb-3">Escolha a sua sessão</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {SESSION_TYPES.map(({ key, label, color, icon: Icon, desc }) => {
            const s = COLOR_STYLES[color];
            return (
              <Card
                key={key}
                data-testid={`session-card-${key}`}
                className={`p-5 ring-1 ${s.ring} ${s.bg} transition-all hover:-translate-y-1 hover:shadow-md`}
              >
                <div className={`w-12 h-12 rounded-full bg-white flex items-center justify-center ${s.text} mb-3 shadow-sm`}>
                  <Icon className="w-6 h-6" />
                </div>
                <h4 className="font-black text-slate-900 text-base">{label}</h4>
                <p className="text-xs text-slate-600 mt-1 mb-4">{desc}</p>
                <Button
                  className={`w-full text-white ${s.btn}`}
                  data-testid={`schedule-${key}`}
                  disabled={balance.balance <= 0}
                  onClick={() => navigate(`/student/professionals?session=${key}`)}
                >
                  <Calendar className="w-4 h-4 mr-1.5" />
                  {balance.balance > 0 ? 'Agendar Sessão' : 'Sem saldo'}
                </Button>
              </Card>
            );
          })}
        </div>
        <p className="text-xs text-slate-500 mt-2">
          Cada sessão utiliza <strong>1 benefício</strong> do seu plano. Após confirmação, o profissional recebe repasse automático.
        </p>
      </div>

      {/* Upcoming appointments */}
      {upcoming.length > 0 && (
        <div>
          <h3 className="text-lg font-bold text-slate-900 mb-3 flex items-center gap-2">
            <CalendarCheck2 className="w-5 h-5 text-emerald-600" /> Próximas Sessões
          </h3>
          <Card className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr className="text-left border-b border-slate-200">
                  <th className="py-2 px-3">Data</th>
                  <th>Horário</th>
                  <th>Profissional</th>
                  <th>Tipo</th>
                  <th>Local</th>
                </tr>
              </thead>
              <tbody data-testid="upcoming-body">
                {upcoming.map((r) => (
                  <tr key={r.request_id} className="border-b border-slate-100">
                    <td className="py-2 px-3 text-xs font-semibold">
                      {r.preferred_date ? new Date(r.preferred_date + 'T00:00:00').toLocaleDateString('pt-BR') : '—'}
                    </td>
                    <td className="text-xs"><Clock className="w-3 h-3 inline mr-1 text-slate-400" />{r.preferred_time || '—'}</td>
                    <td className="font-semibold">{r.professional_name}</td>
                    <td className="text-xs capitalize">{r.session_type || '—'}</td>
                    <td className="text-xs text-slate-500">
                      {r.location ? `${r.location.place_name || ''} ${r.location.city ? '• ' + r.location.city : ''}` : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </div>
      )}

      {/* History */}
      <div>
        <h3 className="text-lg font-bold text-slate-900 mb-3 flex items-center gap-2">
          <HistoryIcon className="w-5 h-5 text-slate-500" /> Histórico Completo
        </h3>
        <Card className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr className="text-left border-b border-slate-200">
                <th className="py-2 px-3">Solicitada em</th>
                <th>Profissional</th>
                <th>Tipo</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody data-testid="benefits-history-body">
              {(history.requests || []).length === 0 ? (
                <tr><td colSpan={4} className="text-center py-6 text-slate-500">Nenhuma utilização ainda</td></tr>
              ) : history.requests.map((r) => (
                <tr key={r.request_id} className="border-b border-slate-100">
                  <td className="py-2 px-3 text-xs">{new Date(r.requested_at).toLocaleDateString('pt-BR')}</td>
                  <td className="font-semibold">{r.professional_name}</td>
                  <td className="text-xs capitalize">{r.session_type || (r.professional_category_ids || []).join(', ')}</td>
                  <td>
                    <span className="text-[10px] uppercase bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded font-bold">
                      {r.status === 'scheduled' ? 'Agendada' : 'Utilizada'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      </div>
    </div>
  );
};

export default BenefitsPage;
